import { apiUrl } from '../utils/api-url';

/**
 * Read client for the customer-scoped server estate (orchestrator RFC §4.5).
 * Server-side paged/filtered/sorted — never load the whole estate client-side.
 */

export interface ServerStateChange {
  state: string;
  at: string;
  by: string;
  note?: string | null;
}

export interface ServerAssessmentSnapshot {
  runId: string;
  assessedAt?: string;
  readiness?: string | null;
  recommendedTarget?: string | null;
  monthlyComputeCost?: number | null;
  monthlyStorageCost?: number | null;
  confidencePercent?: number | null;
  issues?: string[] | null;
}

export interface ServerData {
  hostName: string;
  machineArmId?: string | null;
  source?: string;
  operatingSystem?: string | null;
  cores?: number | null;
  memoryGb?: number | null;
  storageGb?: number | null;
  ipAddresses?: string[];
  migrationState: string;
  stateHistory?: ServerStateChange[];
  latestAssessment?: ServerAssessmentSnapshot | null;
  workloadId?: string | null;
  projectIds?: string[];
  firstDiscoveredAt?: string;
  lastSeenAt?: string;
}

export interface EstateRow {
  id: string;
  customerId: string;
  projectId?: string;
  type: string;
  data?: ServerData;
  createdAt?: string;
  updatedAt?: string;
}

export interface EstateListResult {
  items: EstateRow[];
  total: number;
  page: number;
  pageSize: number;
}

export interface EstateListParams {
  state?: string;
  os?: string;
  projectId?: string;
  search?: string;
  page?: number;
  pageSize?: number;
  sort?: string;
  dir?: 'asc' | 'desc';
}

/** Mirror of the backend's EstateIngestLogic.ConfidenceStars buckets (0-20→1★ … 81-100→5★). */
export function confidenceStarsFromPercent(percent: number | null | undefined): number | null {
  if (percent == null) return null;
  if (percent <= 20) return 1;
  if (percent <= 40) return 2;
  if (percent <= 60) return 3;
  if (percent <= 80) return 4;
  return 5;
}

export async function listEstate(
  customerId: string,
  params: EstateListParams,
  token: string,
): Promise<EstateListResult> {
  const query = new URLSearchParams();
  if (params.state) query.set('state', params.state);
  if (params.os) query.set('os', params.os);
  if (params.projectId) query.set('projectId', params.projectId);
  if (params.search) query.set('search', params.search);
  if (params.page) query.set('page', String(params.page));
  if (params.pageSize) query.set('pageSize', String(params.pageSize));
  if (params.sort) query.set('sort', params.sort);
  if (params.dir) query.set('dir', params.dir);

  const qs = query.toString();
  const res = await fetch(
    apiUrl(`/api/customers/${encodeURIComponent(customerId)}/estate${qs ? `?${qs}` : ''}`),
    { headers: { Authorization: `Bearer ${token}` } },
  );
  if (!res.ok) throw new Error(`Failed to load estate: ${res.status}`);
  return res.json();
}
